import {
  toastUploadError,
  toastUploadResult,
} from 'Features/Projects/Table/uploadToasts'
import { ReplenishmentSiteSku } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/Replenishment/Replenishment.types'
import { toast } from 'react-hot-toast'
import { useMemo } from 'react'
import {
  GridColDef,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium'
import { Box, Button, Tooltip, Typography } from '@mui/material'
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import { useSettingsContext } from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import {
  useUpdateCoupledCycles,
  useUpdateReplenishmentSiteSkus,
  useUploadReplenishmentSiteSkus,
} from './Replenishment.data'

interface ReplenishmentTableProps {
  data: ReplenishmentSiteSku[]
  isLoading: boolean
}

/**
 * Replenishment. A COUPLED row follows its SKU group's cycle in the
 * production template (templateCycleTime, from the API). The cycle is copied
 * in when the row is saved and safety stock reads the copy, so after a
 * template changes the row shows its saved cycle with a warning
 * (cycleOutOfDate) until "Update coupled cycles" copies the new one in.
 */
export const ReplenishmentTable = ({
  data,
  isLoading,
}: ReplenishmentTableProps) => {
  const onSuccess = () => {
    toast.success('Replenishment saved')
  }
  const onError = (message?: string) => {
    toast.error(message || 'The replenishment settings could not be saved')
  }

  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state
  const { mutate, isPending: isSaving } = useUpdateReplenishmentSiteSkus(
    onSuccess,
    onError
  )
  const onUploadSuccess = toastUploadResult
  const onUploadError = toastUploadError
  const { mutate: uploadMutate, isPending: isUploading } =
    useUploadReplenishmentSiteSkus(onUploadSuccess, onUploadError)

  const updateTableData = (
    changedRows: Record<string, ReplenishmentSiteSku>
  ) => {
    mutate({
      changedRows,
      projectId,
      scenarioId,
    })
  }

  const COLUMN_WIDTH = 150
  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: 'left',
      },
      {
        field: 'destinationSiteCode',
        headerName: 'Destination Site Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: 'left',
      },
      {
        field: 'skuGroupCode',
        headerName: 'Sku Group Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: 'left',
      },
      {
        field: 'primaryWorkCentreCode',
        headerName: 'Primary Work Centre Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'planningPeriod',
        headerName: 'Planning Period',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return value
        },
      },
      {
        field: 'supplyTypeCode',
        headerName: 'Supply Type Code',
        width: COLUMN_WIDTH,
        type: 'singleSelect',
        editable: true,
        valueOptions: ['COUPLED', 'DECOUPLED'],
      },
      {
        field: 'replenishmentCycleTime',
        headerName: 'Replenishment Cycle Time',
        width: COLUMN_WIDTH,
        // A select: the cycle is 1, 2, 4 or 8 weeks. As a number column any
        // value could be typed, and the options were ignored.
        type: 'singleSelect',
        editable: true,
        valueOptions: [1, 2, 4, 8],
        // The saved cycle (what safety stock uses), with a warning when a
        // coupled row's template has moved on.
        renderCell: (params: GridRenderCellParams<ReplenishmentSiteSku>) => (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {params.value}
            {params.row.cycleOutOfDate && (
              <Tooltip
                title={`The template cycle is now ${params.row.templateCycleTime} weeks. Update coupled cycles to use it.`}
              >
                <WarningAmberOutlined
                  color="warning"
                  sx={{ fontSize: 16 }}
                  aria-label="Out of date"
                />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        field: 'replenishmentTypeCode',
        headerName: 'Replenishment Type Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'sourceSiteCode',
        headerName: 'Source Site Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'deploymentLeadTime',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
          ...params.props,
          error: !(Number(params.props.value) >= 0),
        }),
        headerName: 'Deployment Lead Time',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return value
        },
      },
      {
        field: 'minimumOrderQuantity',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
          ...params.props,
          error: !(Number(params.props.value) >= 0),
        }),
        headerName: 'Minimum Order Quantity',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return value
        },
      },
      {
        field: 'minimumOrderIncrement',
        headerName: 'Minimum Order Increment',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return value
        },
      },
      {
        field: 'segment',
        headerName: 'Segment',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'serviceLevel',
        headerName: 'Service Level',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return `${(value * 100).toFixed(0)}%`
        },
      },
      // Commented out hidden columns
      // {
      //   field: 'workCentreCode',
      //   headerName: 'Work Centre Code',
      //   width: COLUMN_WIDTH,
      //   type: 'string',
      //   hide: true,
      // },
      // {
      //   field: 'supplyTypeKey',
      //   headerName: 'Supply Type Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'replenishmentTypeKey',
      //   headerName: 'Replenishment Type Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'workCentreKey',
      //   headerName: 'Work Centre Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'baseSourceSiteKey',
      //   headerName: 'Base Source Site Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'destinationSiteKey',
      //   headerName: 'Destination Site Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'id',
      //   headerName: 'Id',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'skuGroupKey',
      //   headerName: 'Sku Group Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'sourceSiteKey',
      //   headerName: 'Source Site Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
    ]
  }, [])

  // Process row updates for the replenishment data
  // A coupled row takes its template's cycle; without a template it can't
  // be coupled, so it stays DECOUPLED.
  const processRow = (newRow: ReplenishmentSiteSku) => {
    if (newRow.supplyTypeCode !== 'COUPLED') return newRow
    if (
      newRow.templateCycleTime === null ||
      newRow.templateCycleTime === undefined
    ) {
      toast(
        `${newRow.skuGroupCode} has no template on its line yet, so it stays decoupled`
      )
      return { ...newRow, supplyTypeCode: 'DECOUPLED' }
    }
    return {
      ...newRow,
      replenishmentCycleTime: newRow.templateCycleTime,
      cycleOutOfDate: false,
    }
  }

  const outOfDate = data.filter((row) => row.cycleOutOfDate).length
  const { mutate: updateCoupled, isPending: isUpdatingCoupled } =
    useUpdateCoupledCycles(
      projectId,
      scenarioId,
      ({ rowsUpdated, siteKeys }) =>
        toast.success(
          `Updated ${rowsUpdated} coupled rows. Safety stock for ${siteKeys.length} site${siteKeys.length === 1 ? '' : 's'} is now out of date.`
        ),
      () => toast.error('Coupled cycles not updated')
    )

  return (
    <>
      {outOfDate > 0 && (
        <Box
          role="status"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}
        >
          <WarningAmberOutlined color="warning" sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="text.secondary">
            {outOfDate} coupled row{outOfDate === 1 ? ' has' : 's have'} a newer
            template cycle than the one saved. Safety stock uses the saved one.
          </Typography>
          <Button
            size="small"
            onClick={() => updateCoupled()}
            disabled={isUpdatingCoupled}
          >
            Update coupled cycles
          </Button>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'auto',
        }}
      >
        <BaseTable
          data={data}
          columns={columns}
          isLoading={isLoading ?? false}
          updateTableData={updateTableData}
          uploadData={(file: File) => {
            uploadMutate({ file, projectId, scenarioId })
          }}
          isUploading={isUploading ?? false}
          customDownloadDataMethod={(tableData) => tableData}
          isSaving={isSaving ?? false}
          checkBoxSelection={true}
          processRow={processRow}
          pinnedColumns={[
            'baseSourceSiteCode',
            'destinationSiteCode',
            'skuGroupCode',
          ]}
        />
      </Box>
    </>
  )
}
